import React, { useState, useEffect } from 'react';

import {
    apiFetchTimesheet,
    apiFetchPayroll,
    apiFetchHolidays,
    apiFetchLuongT13,
    apiFetchUnreadNotifications,
    apiMarkNotificationAsRead,
} from './../api'; // Đảm bảo đường dẫn chính xác

import ChangePasswordModal from '../components/common/ChangePasswordModal';
import NotificationViewer from '../components/common/NotificationViewer'; 
import NotificationHistoryModal from '../components/common/NotificationHistoryModal'; 
import TimesheetTable from '../components/timesheet/TimesheetTable';
import PayrollDetails from '../components/payroll/PayrollDetails';
import HolidayTable from '../components/leave/HolidayTable';
import LuongT13Details from '../components/payroll/LuongT13Details';

// --- Hàm tính thâm niên làm việc ---
const calculateDynamicWorkDuration = (startDate) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const now = new Date();
    if (start > now) return 'Nhân viên mới';
    
    let years = now.getFullYear() - start.getFullYear();
    let months = now.getMonth() - start.getMonth();
    let days = now.getDate() - start.getDate();
    
    if (days < 0) {
        months--;
        const prevMonthLastDay = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        days += prevMonthLastDay;
    }
    if (months < 0) {
        years--;
        months += 12;
    }
    
    const parts = [];
    if (years > 0) parts.push(`${years} năm`);
    if (months > 0) parts.push(`${months} tháng`);
    if (days > 0) parts.push(`${days} ngày`);
    return parts.length > 0 ? parts.join(' ') : 'Hôm nay';
};

// --- Component chính ---
export default function EmployeeDashboard({ user = { id: 1, name: 'Nhân Viên', indat: '2024-01-01' }, onLogout = () => alert('Đăng xuất') }) {
    // States điều hướng giao diện (Mobile Tabs)
    const [view, setView] = useState('overview'); 
    
    // States thời gian
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    
    // States lưu trữ dữ liệu từ API
    const [timesheetData, setTimesheetData] = useState([]);
    const [payrollData, setPayrollData] = useState(null);
    const [holidayData, setHolidayData] = useState([]);
    const [holidaySummary, setHolidaySummary] = useState({ remaining: 0, isCurrentYear: true, isForeigner: false });
    const [luongT13Data, setLuongT13Data] = useState(null);
    
    // States trạng thái UI & Modal
    const [isLoading, setIsLoading] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [dynamicWorkDuration, setDynamicWorkDuration] = useState('');
    const [unreadNotifications, setUnreadNotifications] = useState([]);
    const [showNotificationHistory, setShowNotificationHistory] = useState(false);
    
    // Tính toán và cập nhật thâm niên mỗi ngày
    useEffect(() => {
        if (user && user.indat) {
            setDynamicWorkDuration(calculateDynamicWorkDuration(user.indat));
            const intervalId = setInterval(() => {
                setDynamicWorkDuration(calculateDynamicWorkDuration(user.indat));
            }, 1000 * 60 * 60 * 24);
            return () => clearInterval(intervalId);
        }
    }, [user]);

    // Tải thông báo khi có user mới
    useEffect(() => {
        if (user && user.id) {
            apiFetchUnreadNotifications(user.id)
                .then(res => setUnreadNotifications(res || []))
                .catch(err => console.error("Lỗi tải thông báo chưa đọc:", err));
        }
    }, [user]);

    // Tải dữ liệu chính mỗi khi user, tháng, hoặc năm thay đổi
    useEffect(() => {
        if (!user || !user.id) return;
        
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const monthString = (currentMonth + 1).toString().padStart(2, '0');
                const yearMonth = `${currentYear}-${monthString}`;
                
                const [timesheetRes, payrollRes, holidayRes, luongRes] = await Promise.all([
                    apiFetchTimesheet(user.id, yearMonth).catch(() => []),
                    apiFetchPayroll(user.id, yearMonth).catch(() => null),
                    apiFetchHolidays(user.id, currentYear).catch(() => ({ holidayList: [], summary: { remaining: 0 } })),
                    apiFetchLuongT13(user.id, currentYear).catch(() => null)
                ]);

                setTimesheetData(timesheetRes || []);
                setPayrollData(payrollRes);
                if (holidayRes) {
                    setHolidayData(holidayRes.holidayList || []);
                    setHolidaySummary(holidayRes.summary || { remaining: 0 });
                }
                setLuongT13Data(luongRes);
            } catch (err) {
                console.error("Lỗi khi gọi API dữ liệu nhân sự:", err);
            }
            setIsLoading(false);
        };
        fetchData();
    }, [user, currentYear, currentMonth]);

    // Xử lý đóng thông báo
    const handleCloseNotification = async (notificationId) => {
        if (!user || !user.id) return;
        try {
            await apiMarkNotificationAsRead(user.id, notificationId);
        } catch (error) {
            console.error("Lỗi đánh dấu đã đọc thông báo:", error);
        } finally {
            setUnreadNotifications(prev => prev.filter(n => n.NotificationID !== notificationId));
        }
    };

    // Dữ liệu cho các thanh Select Option
    const startYear = new Date().getFullYear() + 1;
    const years = Array.from({ length: 5 }, (_, i) => startYear - i);
    const months = Array.from({ length: 12 }, (_, i) => ({ value: i, name: `Tháng ${i + 1}` }));
    
    // Format tiền tệ VNĐ
    const formatter = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' });

    const renderTabContent = () => {
        if (isLoading) {
            return (
                <div className="flex flex-col items-center justify-center py-20 space-y-3">
                    <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-xs text-gray-400 font-medium">Đang tải dữ liệu từ hệ thống...</p>
                </div>
            );
        }

        switch (view) {
            case 'timesheet':
                return (
                    <div className="space-y-4 animate-fade-in">
                        <div className="flex justify-between items-center px-1">
                            <h2 className="text-base font-bold text-gray-900">Bảng Chấm Công</h2>
                            <span className="text-xs text-gray-500 font-semibold">Tháng {currentMonth + 1}/{currentYear}</span>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-3xl p-4 overflow-x-auto shadow-sm">
                            <TimesheetTable data={timesheetData} />
                        </div>
                    </div>
                );
            case 'payroll':
                return (
                    <div className="space-y-4 animate-fade-in">
                        <div className="flex justify-between items-center px-1">
                            <h2 className="text-base font-bold text-gray-900">Chi Tiết Lương</h2>
                            <span className="text-xs text-gray-500 font-semibold">Tháng {currentMonth + 1}/{currentYear}</span>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-3xl p-5 shadow-sm">
                            <PayrollDetails data={payrollData} />
                        </div>
                    </div>
                );
            case 'holiday':
                return (
                    <div className="space-y-4 animate-fade-in">
                        <div className="flex justify-between items-center px-1">
                            <h2 className="text-base font-bold text-gray-900">Nghỉ Phép Năm</h2>
                            <span className="text-xs text-gray-500 font-semibold">Năm {currentYear}</span>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-3xl p-5 shadow-sm">
                            <HolidayTable data={holidayData} summary={holidaySummary} />
                        </div>
                    </div>
                );
            case 'luongT13':
                return (
                    <div className="space-y-4 animate-fade-in">
                        <div className="flex justify-between items-center px-1">
                            <h2 className="text-base font-bold text-gray-900">Thưởng Lương Tháng 13</h2>
                            <span className="text-xs text-gray-500 font-semibold">Năm {currentYear}</span>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-3xl p-5 shadow-sm">
                            <LuongT13Details data={luongT13Data} year={currentYear} />
                        </div>
                    </div>
                );
            default:
                // 1. Logic Trạng Thái Lương (Đã duyệt vs Chưa duyệt)
                const statusText = payrollData?.status || (payrollData?.approved === false ? 'Chờ duyệt' : 'Đã duyệt');
                const isApproved = statusText.toLowerCase() === 'đã duyệt' || statusText.toLowerCase() === 'paid';
                const statusDotColor = isApproved ? 'bg-emerald-500' : 'bg-amber-500';
                const statusTextColor = isApproved ? 'text-gray-500' : 'text-amber-600';

                // 2. Logic Tổng giờ làm thực tế của tháng
                const totalHours = timesheetData?.reduce((acc, row) => acc + (Number(row.hoursWorked) || 0), 0) || 0;

                // 3. Logic Tổng Tăng ca (OT) dựa trên các key chính xác: h1, h2, h3, b3, b4, b5
                const totalOT = timesheetData?.reduce((acc, row) => {
                    const ot = Number(row.h1 || 0) + 
                               Number(row.h2 || 0) + 
                               Number(row.h3 || 0);
                    return acc + ot;
                }, 0) || 0;

                return (
                    <div className="space-y-4 animate-fade-in">
                        
                        {/* --- BENTO CARD: TỔNG QUAN LƯƠNG --- */}
                        <div className="bg-gradient-to-br from-emerald-50 via-emerald-50/60 to-emerald-50 border border-emerald-100 rounded-[2rem] p-5 shadow-sm relative overflow-hidden">
                            <div className="absolute -right-8 -top-8 w-32 h-32 bg-emerald-400/10 rounded-full blur-2xl pointer-events-none"></div>
                            
                            <div className="mb-4">
                                <span className="bg-emerald-200/50 text-emerald-800 border border-emerald-200/50 text-xs font-bold px-3 py-1.5 rounded-full inline-block">
                                    Tháng {currentMonth + 1}, {currentYear}
                                </span>
                            </div>
                            
                            {/* Hiển thị linh hoạt Lương Chính Thức / Lương Dự Kiến */}
                            <p className="text-sm text-gray-600 font-medium">
                                {isApproved ? 'Lương chính thức' : 'Lương thực nhận dự kiến'}
                            </p>
                            
                            <h2 className="text-4xl font-black text-emerald-600 tracking-tight mt-1 mb-5">
                                {payrollData?.summary?.luongThucLanh ? formatter.format(payrollData.summary.luongThucLanh) : 'Đang cập nhật'}
                            </h2>
                            
                            <div className="pt-4 border-t border-emerald-200/70 flex items-center justify-between text-sm">
                                <span className={`flex items-center gap-1.5 font-medium ${statusTextColor}`}>
                                    <span className={`w-2.5 h-2.5 rounded-full ${statusDotColor}`}></span>
                                    Trạng thái: {statusText}
                                </span>
                                <button onClick={() => setView('payroll')} className="text-emerald-700 font-bold hover:text-emerald-800 transition-colors flex items-center gap-1">
                                    Chi tiết <span aria-hidden="true">&rarr;</span>
                                </button>
                            </div>
                        </div>

                        {/* --- BENTO GRID: 2 CỘT (CHẤM CÔNG & NGHỈ PHÉP) --- */}
                        <div className="grid grid-cols-2 gap-3">
                            {/* Cột 1: Chấm công & Tăng ca */}
                            <div onClick={() => setView('timesheet')} className="bg-white border border-gray-200 rounded-3xl p-5 flex flex-col justify-between active:scale-[0.98] transition cursor-pointer shadow-sm hover:shadow-md">
                                <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-500 flex items-center justify-center font-bold mb-4 shadow-sm">⏱️</div>
                                <div>
                                    <div className="flex items-baseline gap-1.5">
                                        <h3 className="text-2xl font-black text-gray-900">{totalHours.toFixed(1)}h</h3>
                                        {/* Hiển thị số giờ Tăng ca (OT) */}
                                        <span className={`text-xs font-bold ${totalOT > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                                            Gồm {totalOT > 0 ? totalOT.toFixed(1) : '0'}h OT
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-gray-500 font-medium mt-0.5">Giờ làm tháng này</p>
                                    <span className="inline-block mt-3 px-2.5 py-1 rounded-full text-[10px] font-bold bg-blue-50 text-blue-600 border border-blue-100">
                                        Xem chấm công
                                    </span>
                                </div>
                            </div>

                            {/* Cột 2: Nghỉ phép */}
                            <div onClick={() => setView('holiday')} className="bg-white border border-gray-200 rounded-3xl p-5 flex flex-col justify-between active:scale-[0.98] transition cursor-pointer shadow-sm hover:shadow-md">
                                <div className="w-10 h-10 rounded-full bg-purple-50 text-purple-500 flex items-center justify-center font-bold mb-4 shadow-sm">🌴</div>
                                <div>
                                    <h3 className="text-2xl font-black text-gray-900">{holidaySummary?.remaining ?? 0} Giờ</h3>
                                    <p className="text-[11px] text-gray-500 font-medium mt-0.5">Phép năm còn lại</p>
                                    <span className="inline-block mt-3 px-2.5 py-1 rounded-full text-[10px] font-bold bg-purple-50 text-purple-600 border border-purple-100">
                                        Quản lý nghỉ phép
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* --- BENTO CARD: THƯỞNG THÁNG 13 --- */}
                        <div onClick={() => setView('luongT13')} className="bg-white border border-gray-200 rounded-3xl p-5 flex items-center justify-between active:scale-[0.98] transition cursor-pointer shadow-sm hover:shadow-md">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center text-xl shadow-sm">🎁</div>
                                <div>
                                    <h4 className="font-bold text-sm text-gray-900">Thưởng Tháng 13</h4>
                                    <p className="text-[11px] text-gray-500 mt-0.5">Dự kiến chi trả cuối năm</p>
                                </div>
                            </div>
                            <span className="text-xs font-bold text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-100">Xem ngay</span>
                        </div>

                        {/* --- QUICK ACTIONS BAR --- */}
                        <div className="grid grid-cols-3 gap-3 pt-2">
                            <a href="https://zalo.me/0367517649" target="_blank" rel="noopener noreferrer" className="bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-bold py-3.5 px-2 rounded-2xl text-center flex items-center justify-center gap-1 transition-colors">
                                💬 Zalo
                            </a>
                            <button onClick={() => setShowChangePassword(true)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold py-3.5 px-2 rounded-2xl text-center flex items-center justify-center gap-1 transition-colors">
                                🔑 Đổi MK
                            </button>
                            <button onClick={onLogout} className="bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs font-bold py-3.5 px-2 rounded-2xl text-center flex items-center justify-center gap-1 transition-colors">
                                🚪 Đăng xuất
                            </button>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="bg-slate-50 text-gray-900 min-h-screen font-sans antialiased pb-28 selection:bg-emerald-200 selection:text-emerald-900">
            {/* Top Header */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200 px-4 py-4 flex justify-between items-center shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <img 
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'Nhân Viên')}&background=10b981&color=fff&rounded=true&bold=true`} 
                            alt="Avatar" 
                            className="w-12 h-12 rounded-full border-2 border-emerald-100 object-cover shadow-sm" 
                        />
                        <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full"></div>
                    </div>
                    <div>
                        <p className="text-[11px] text-gray-500 font-medium leading-tight mb-0.5">Xin chào,</p>
                        <h1 className="text-base font-bold text-gray-900 tracking-tight leading-tight">{user?.name || 'Nhân viên'} 👋</h1>
                        {/* Thâm niên làm việc */}
                        <p className="text-[10px] text-gray-400 mt-0.5 font-medium">{dynamicWorkDuration}</p>
                    </div>
                </div>

                <button 
                    onClick={() => setShowNotificationHistory(true)} 
                    className="relative w-10 h-10 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-600 active:scale-95 transition hover:bg-gray-100"
                >
                    🔔
                    {unreadNotifications.length > 0 && (
                        <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-rose-500 rounded-full ring-2 ring-white animate-pulse"></span>
                    )}
                </button>
            </header>

            {/* Main Content Area */}
            <main className="px-4 pt-5 space-y-5 max-w-md mx-auto">
                {/* Selectors Tháng/Năm */}
                <div className="grid grid-cols-2 gap-3 mb-2">
                    <div className="relative">
                        <select 
                            value={currentMonth} 
                            onChange={e=>setCurrentMonth(parseInt(e.target.value))} 
                            className="w-full appearance-none bg-white border border-gray-200 rounded-2xl py-3 pl-4 pr-10 text-sm text-gray-900 font-bold outline-none shadow-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        >
                            {months.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </div>
                    
                    <div className="relative">
                        <select 
                            value={currentYear} 
                            onChange={e=>setCurrentYear(parseInt(e.target.value))} 
                            className="w-full appearance-none bg-white border border-gray-200 rounded-2xl py-3 pl-4 pr-10 text-sm text-gray-900 font-bold outline-none shadow-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                        >
                            {years.map(y => <option key={y} value={y}>Năm {y}</option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </div>
                </div>

                {renderTabContent()}
            </main>

            {/* Modals & Notifications */}
            <NotificationViewer notifications={unreadNotifications} onClose={handleCloseNotification} />
            {showNotificationHistory && <NotificationHistoryModal user={user} onClose={() => setShowNotificationHistory(false)} />}
            {showChangePassword && <ChangePasswordModal user={user} onClose={() => setShowChangePassword(false)} />}

            {/* Fixed Mobile Bottom Navigation Bar */}
            <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-t border-gray-200 px-3 py-2 shadow-[0_-4px_6px_-1px_rgb(0,0,0,0.05)]">
                <div className="max-w-md mx-auto flex justify-around items-center">
                    {[
                        { id: 'overview', label: 'Tổng quan', icon: '🏠' },
                        { id: 'timesheet', label: 'Chấm công', icon: '⏱️' },
                        { id: 'payroll', label: 'Lương', icon: '💰' },
                        { id: 'holiday', label: 'Nghỉ phép', icon: '🌴' },
                        { id: 'luongT13', label: 'Thưởng', icon: '🎁' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setView(tab.id)}
                            className={`flex flex-col items-center gap-1 py-2 px-3 rounded-2xl transition-all ${view === tab.id ? 'text-emerald-600 bg-emerald-50 font-bold scale-105' : 'text-gray-400 hover:text-gray-600 font-medium'}`}
                        >
                            <span className="text-lg grayscale-[20%]">{tab.icon}</span>
                            <span className="text-[10px] tracking-wide">{tab.label}</span>
                        </button>
                    ))}
                </div>
            </nav>

            <style>{`
                @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fade-up { animation: fadeUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in { animation: fadeIn 0.25s ease-out forwards; }
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
}